/**
 * Fasada analityki frontowej. JEDYNE miejsce, w którym aplikacja dotyka
 * `window.dataLayer` — reszta kodu woła `track("nazwa_eventu", {...})`.
 *
 * Kanałem jest Google Tag Manager (kontener GTM-W4XL9L4P, ładowany w index.html —
 * ten sam co strona marketingowa v1). Konkretne tagi (GA4, Meta Pixel, Clarity,
 * konwersje Ads) konfiguruje panel GTM z triggerami na nazwy eventów stąd; kod
 * o providerach nie wie i nie ładuje ich skryptów.
 *
 * Zgoda: Consent Mode v2. Stan domyślny (wszystko `denied`) ustawia inline snippet
 * w index.html PRZED GTM, a `setConsent()` robi `consent update`. Pushujemy do
 * dataLayer niezależnie od zgody — dopóki jej nie ma, tagi Google nie odpalają
 * i nic nie zapisują.
 *
 * Fire-and-forget: nigdy nie rzuca, nie blokuje operacji biznesowej.
 */

/**
 * Decyzja o zgodzie żyje w COOKIE, nie w localStorage.
 *
 * Powód: `localStorage` jest per origin, a `adresflow.com` i `app.adresflow.com`
 * to dwa różne originy — użytkownik, który zaakceptował na stronie marketingowej,
 * dostawał baner drugi raz zaraz po przejściu do logowania. Cookie z
 * `domain=.adresflow.com` obejmuje obie domeny.
 *
 * Nazwa i wartości MUSZĄ być identyczne po obu stronach: tu, w inline snippecie
 * w `index.html`, oraz w `src/lib/consent.ts` i `index.html` w repo v1.
 */
const CONSENT_COOKIE = "adresflow_consent";

/** Ile trzymamy decyzję. Pół roku — tyle, ile typowo przyjmuje się dla zgody RODO. */
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

/**
 * Klucz localStorage sprzed wprowadzenia cookie. Czytamy go nadal, żeby nie pytać
 * drugi raz nikogo, kto zdążył już zdecydować w tym panelu; zapisujemy jako
 * zapasowy kanał, gdy przeglądarka blokuje cookies.
 */
const LEGACY_CONSENT_KEY = "af_analytics_consent";

export type ConsentChoice = "granted" | "denied";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: Gtag;
  }
}

/**
 * Domenę ustawiamy TYLKO na produkcji. Na `localhost` i domenach podglądowych
 * Vercela przeglądarka odrzuciłaby cookie z obcą domeną — po cichu, bez błędu —
 * i baner wracałby przy każdym odświeżeniu.
 */
function consentCookieDomain(): string {
  const host = window.location.hostname;
  return host === "adresflow.com" || host.endsWith(".adresflow.com") ? "; domain=.adresflow.com" : "";
}

function readConsentCookie(): ConsentChoice | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=(granted|denied)`));
  return match ? (match[1] as ConsentChoice) : null;
}

function writeConsentCookie(choice: ConsentChoice): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${CONSENT_COOKIE}=${choice}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax` +
    secure +
    consentCookieDomain();
}

/** Zapisana decyzja usera albo null, gdy jeszcze nie wybrał. */
export function getConsent(): ConsentChoice | null {
  const fromCookie = readConsentCookie();
  if (fromCookie) return fromCookie;

  // Decyzja sprzed wprowadzenia cookie — przepisujemy ją, żeby nie pytać drugi raz.
  try {
    const legacy = localStorage.getItem(LEGACY_CONSENT_KEY);
    if (legacy === "granted" || legacy === "denied") {
      writeConsentCookie(legacy);
      return legacy;
    }
  } catch {
    /* tryb prywatny / storage zablokowany */
  }
  return null;
}

/**
 * Zapisuje decyzję i wysyła `consent update` do GTM. Sygnały reklamowe idą razem
 * z analitycznymi — banner ma tylko „akceptuję" / „odmawiam".
 */
export function setConsent(choice: ConsentChoice): void {
  writeConsentCookie(choice);
  // Zapasowo także w localStorage: gdy przeglądarka blokuje cookies, decyzja i tak
  // przetrwa w obrębie tego panelu (między domenami wtedy nie przejdzie — trudno).
  try {
    localStorage.setItem(LEGACY_CONSENT_KEY, choice);
  } catch {
    /* brak storage — mimo to aktualizujemy zgodę na tę sesję */
  }

  // gtag zdefiniowany w index.html; fallback dla ścieżek, gdzie snippet nie
  // wystartował (blokada skryptów, JSDOM w testach).
  //
  // MUSI pushować `arguments`, nie tablicę: GTM rozpoznaje komendę po tym, że
  // wpis jest obiektem Arguments. Zwykła tablica ląduje w dataLayer jako DANE
  // i `consent update` przepada — setConsent zgłasza sukces, a kontener zostaje
  // na `denied` na zawsze. Stąd `function`, nie strzałka ze spreadem.
  const push: Gtag =
    typeof window.gtag === "function"
      ? window.gtag
      : function () {
          window.dataLayer = window.dataLayer || [];
          (window.dataLayer as unknown[]).push(arguments);
        };

  push("consent", "update", {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  });

  // PO `consent update`, nie przed — inaczej dosyłane zdarzenia trafiłyby do GTM
  // wciąż z `analytics_storage: denied` i przepadły drugi raz.
  if (choice === "granted") {
    flushPending("persistent");
    flushPending("session");
  }
}

/** Push zdarzenia do dataLayer. GTM decyduje, które tagi na nie zareagują. */
export function track(event: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...(params ?? {}) });
  } catch {
    /* dataLayer niedostępny (blokada rozszerzeniem) — ignorujemy */
  }
}

/**
 * Zasięg dedupe: `persistent` przeżywa zamknięcie przeglądarki (localStorage),
 * `session` żyje w obrębie jednej karty (sessionStorage).
 */
type DedupeScope = "persistent" | "session";

type PendingEvent = { event: string; dedupeKey: string; params?: Record<string, unknown> };

/** Kolejka zdarzeń złapanych przy `denied` — czeka na zgodę, potem leci do GTM. */
const PENDING_KEY = "af_evt_pending";

function storageFor(scope: DedupeScope): Storage {
  return scope === "session" ? sessionStorage : localStorage;
}

function readPending(scope: DedupeScope): PendingEvent[] {
  try {
    const raw = storageFor(scope).getItem(PENDING_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as PendingEvent[]) : [];
  } catch {
    return []; // brak storage albo uszkodzony wpis — kolejka po prostu pusta
  }
}

function writePending(scope: DedupeScope, events: PendingEvent[]): void {
  try {
    storageFor(scope).setItem(PENDING_KEY, JSON.stringify(events));
  } catch {
    /* brak storage — zdarzenie przepada, nie ma gdzie go odłożyć */
  }
}

/**
 * Wysyła zdarzenie najwyżej raz na `dedupeKey`.
 *
 * Konieczne dla sign_up: Supabase emituje SIGNED_IN przy KAŻDYM odzyskaniu sesji,
 * także przy powrocie do karty, a heurystyka „nowe konto" (created_at ≈
 * last_sign_in_at) nie wygasa od razu. Bez dedupe jedna rejestracja raportowałaby
 * się wielokrotnie.
 *
 * Bez zgody zdarzenie idzie do KOLEJKI, nie do GTM: przy `denied` tagi Google nie
 * odpalają i push przepada bezgłośnie. Dotyczy to typowej ścieżki nowego usera —
 * rejestracja przez Google wraca z accounts.google.com przed kliknięciem bannera,
 * a `purchase` potrafi wyprzedzić decyzję o całą wizytę w Stripe.
 */
function trackDeduped(
  event: string,
  dedupeKey: string,
  params: Record<string, unknown> | undefined,
  scope: DedupeScope,
): void {
  const sentKey = `af_evt_${dedupeKey}`;
  let store: Storage;
  try {
    store = storageFor(scope);
    if (store.getItem(sentKey)) return;
  } catch {
    track(event, params); // brak storage — lepiej bez dedupe niż cisza
    return;
  }

  if (getConsent() !== "granted") {
    const pending = readPending(scope);
    if (pending.some((queued) => queued.dedupeKey === dedupeKey)) return;
    pending.push({ event, dedupeKey, params });
    writePending(scope, pending);
    return;
  }

  try {
    store.setItem(sentKey, "1");
  } catch {
    /* wyścig z czyszczeniem storage — wysyłamy mimo to */
  }
  track(event, params);
}

/** Opróżnia kolejkę po udzieleniu zgody. Wywoływane wyłącznie z setConsent(). */
function flushPending(scope: DedupeScope): void {
  const pending = readPending(scope);
  if (pending.length === 0) return;
  // Czyścimy PRZED wysyłką: trackDeduped zdąży odłożyć zdarzenie z powrotem,
  // gdyby zgoda w międzyczasie zniknęła, a tak nie zapętlimy tej samej listy.
  writePending(scope, []);
  for (const queued of pending) {
    trackDeduped(queued.event, queued.dedupeKey, queued.params, scope);
  }
}

/** Zdarzenie raz na użytkownika/zakup — trwale (localStorage). */
export function trackOnce(event: string, dedupeKey: string, params?: Record<string, unknown>): void {
  trackDeduped(event, dedupeKey, params, "persistent");
}

/** Jak trackOnce, ale w obrębie jednej karty — dla `login`, który ma lecieć raz na
 *  sesję przeglądarki, nie raz na każdy powrót do karty. */
export function trackOncePerSession(event: string, dedupeKey: string, params?: Record<string, unknown>): void {
  trackDeduped(event, dedupeKey, params, "session");
}
